/**
 * CustomerDocuments (DOCS-TAB, Seth 2026-06-11: "Documents should be its own
 * tab") — the customer's full file library across ALL properties: photo grid,
 * document list, name search, kind filter, upload, and the FILES-04 preview
 * modal (view / download / email / text). The Overview card stays as the
 * at-a-glance summary; this tab is the deep library — ServiceTitan keeps the
 * same split (record summary + Photos/Attachments sections).
 */
import { useMemo, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { FileText, FolderOpen, ImagePlus, Loader2, Search } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import {
  useCustomerFiles,
  useSignedUrls,
  useUploadFiles,
  prettySize,
  type PegasusFile,
} from "@/features/files/filesCore";
import { ArchiveFileButton } from "@/features/files/ArchiveFileButton";
import { FilePreviewModal } from "@/features/files/FilePreviewModal";
import type { LocationRow } from "./api";

type KindFilter = "all" | "photo" | "document";

export function CustomerDocuments({
  custno,
  locno,
  locations,
}: {
  custno: string;
  locno?: string | null;
  locations: LocationRow[];
}) {
  const { data, isLoading } = useCustomerFiles(custno);
  const all = data ?? [];
  const upload = useUploadFiles({ custno, locno });
  const inputRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<KindFilter>("all");
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState<PegasusFile | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const locLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of locations) m.set(l.locno, l.add1 ?? l.locname ?? l.locno);
    return m;
  }, [locations]);
  const multiProperty = locations.length > 1;

  const filtered = all.filter(
    (f) =>
      (kind === "all" || f.kind === kind) &&
      (!q.trim() || f.filename.toLowerCase().includes(q.trim().toLowerCase())),
  );
  const photos = filtered.filter((f) => f.kind === "photo");
  const docs = filtered.filter((f) => f.kind === "document");
  const urls = useSignedUrls(photos.slice(0, 60)).data;

  function onPicked(list: FileList | null) {
    if (!list || list.length === 0) return;
    setNotice(null);
    upload.mutate([...list], {
      onSuccess: (r) =>
        setNotice(r.skipped.length ? `Skipped: ${r.skipped.join(", ")}` : null),
      onError: (e) => setNotice(`Upload failed — ${(e as Error).message}`),
    });
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div className="space-y-4">
      <FilePreviewModal file={preview} custno={custno} onClose={() => setPreview(null)} />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center overflow-hidden rounded-lg border border-hairline bg-white shadow-e1">
          {(
            [
              ["all", `All ${all.length}`],
              ["photo", `Photos ${all.filter((f) => f.kind === "photo").length}`],
              ["document", `Documents ${all.filter((f) => f.kind === "document").length}`],
            ] as const
          ).map(([k, label], i) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={cn(
                "px-3 py-1.5 text-xs font-semibold transition-colors",
                i > 0 && "border-l border-hairline",
                kind === k ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search filenames…"
            className="rounded-lg border border-hairline bg-white py-1.5 pl-7 pr-2 text-sm shadow-e1 focus:border-brand-400 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
        >
          {upload.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ImagePlus className="h-3.5 w-3.5" />
          )}
          {upload.isPending ? "Uploading…" : "Add files"}
        </button>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => onPicked(e.target.files)} />
      </div>
      {notice && (
        <p className={cn("text-xs", notice.startsWith("Upload failed") ? "text-red-600" : "text-amber-700")}>
          {notice}
        </p>
      )}

      {isLoading ? (
        <Card className="h-40 animate-pulse bg-slate-100" />
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <FolderOpen className="mx-auto h-7 w-7 text-slate-300" />
          <p className="mt-2 text-sm font-medium text-slate-600">
            {q.trim() ? `Nothing matches “${q.trim()}”` : "No files on this customer yet"}
          </p>
        </Card>
      ) : (
        <>
          {photos.length > 0 && (kind === "all" || kind === "photo") && (
            <Card className="p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Photos · {photos.length}
              </h3>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                {photos.slice(0, 60).map((f) => (
                  <div key={f.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => setPreview(f)}
                      title={`${f.filename} · ${format(parseISO(f.created_at), "MMM d, yyyy")}`}
                      className="relative block h-24 w-full overflow-hidden rounded-lg bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                    >
                      {urls?.get(f.storage_path) ? (
                        <img
                          src={urls.get(f.storage_path)}
                          alt={f.caption ?? f.filename}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                        />
                      ) : (
                        <span className="flex h-full items-center justify-center text-slate-300">
                          <ImagePlus className="h-5 w-5" />
                        </span>
                      )}
                    </button>
                    {/* 360-AUDIT wave 2 (A): visible at rest + two-click confirm. */}
                    <ArchiveFileButton fileId={f.id} filename={f.filename} look="overlay" />
                  </div>
                ))}
              </div>
              {photos.length > 60 && (
                <p className="mt-2 text-xs text-slate-400">
                  Showing the newest 60 — search to narrow.
                </p>
              )}
            </Card>
          )}

          {docs.length > 0 && (kind === "all" || kind === "document") && (
            <Card className="overflow-hidden">
              <h3 className="border-b border-slate-100 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Documents · {docs.length}
              </h3>
              <ul className="divide-y divide-slate-50">
                {docs.map((f) => (
                  <li key={f.id} className="group flex items-center gap-2 px-2 py-1">
                    <button
                      type="button"
                      onClick={() => setPreview(f)}
                      className="flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-slate-50"
                    >
                      <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                        {f.filename}
                      </span>
                      {multiProperty && f.locno && (
                        <span className="hidden shrink-0 truncate rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500 sm:inline">
                          {locLabel.get(f.locno) ?? f.locno}
                        </span>
                      )}
                      {f.source === "esc" && (
                        <span className="shrink-0 rounded bg-slate-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                          esc
                        </span>
                      )}
                      <span className="shrink-0 text-xs tabular-nums text-slate-400">
                        {prettySize(f.size_bytes)}
                      </span>
                      <span className="hidden w-24 shrink-0 text-right text-xs tabular-nums text-slate-400 sm:inline">
                        {format(parseISO(f.created_at), "MMM d, yyyy")}
                      </span>
                    </button>
                    <ArchiveFileButton fileId={f.id} filename={f.filename} look="row" />
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
